import { MyProgress, tSharedAppState } from 'common/modules/ReactShared';
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  IMyProgressState,
  ILeaderboardState,
  tMyProgressFlags,
  tStatsPeriod,
  tLeaderboardDimension,
  leaderboardKey
} from '../../../models/myProgress';

const DEFAULT_STATS_PERIOD: tStatsPeriod = 'week';

const noFlags = (): tMyProgressFlags => ({
  stats: false,
  rewards: false,
  vocabulary: false,
  comparative: false,
  goals: false
});

const initialState: IMyProgressState = {
  stats: null,
  statsPeriod: DEFAULT_STATS_PERIOD,
  statsRequestId: null,
  rewards: [],
  vocabulary: null,
  comparative: null,
  goals: [],
  leaderboard: { boards: {}, loading: null, failed: null } as ILeaderboardState,
  loading: noFlags(),
  fetched: noFlags(),
  failed: noFlags()
};

const getProgressStats = createAsyncThunk<
  MyProgress.MyProgressApiTypes.IProgressStatsResponse,
  tStatsPeriod | undefined,
  { state: tSharedAppState }
>('myProgress/getProgressStats_request', async (period) => {
  return MyProgress.myProgressApi.getProgressStats(period);
});

const getRewards = createAsyncThunk<
  MyProgress.MyProgressApiTypes.IRewardsResponse,
  void,
  { state: tSharedAppState }
>('myProgress/getRewards_request', async () => {
  return MyProgress.myProgressApi.getRewards();
});

const getVocabularyProgress = createAsyncThunk<
  MyProgress.MyProgressApiTypes.IVocabularyProgressResponse,
  void,
  { state: tSharedAppState }
>('myProgress/getVocabularyProgress_request', async () => {
  return MyProgress.myProgressApi.getVocabularyProgress();
});

const getComparativeStat = createAsyncThunk<
  MyProgress.MyProgressApiTypes.IComparativeStatResponse,
  void,
  { state: tSharedAppState }
>('myProgress/getComparativeStat_request', async () => {
  return MyProgress.myProgressApi.getComparativeStat();
});

const getGoals = createAsyncThunk<
  MyProgress.MyProgressApiTypes.IGoalsResponse,
  void,
  { state: tSharedAppState }
>('myProgress/getGoals_request', async () => {
  return MyProgress.myProgressApi.getGoals();
});

const getLeaderboard = createAsyncThunk<
  MyProgress.MyProgressApiTypes.ILeaderboardResponse,
  { dimension: tLeaderboardDimension; period: tStatsPeriod },
  { state: tSharedAppState }
>('myProgress/getLeaderboard_request', async (params) => {
  return MyProgress.myProgressApi.getLeaderboard(params);
});

const myProgressSlice = createSlice({
  name: 'myProgress',
  initialState,
  reducers: {
    /**
     * Drops every cached board. Used when something the boards were built from
     * changes underneath them — the student hiding or unhiding their profile
     * changes what their own standing says, and a board cached a moment earlier
     * would keep saying the opposite.
     */
    leaderboardInvalidated(state) {
      state.leaderboard = { boards: {}, loading: null, failed: null };
    }
  },
  extraReducers: (builder) => {
    // Stats
    // The period lives here rather than in the component: the picker label has
    // to survive a remount, and only the newest request may settle the section —
    // switching periods quickly otherwise lets a slower earlier response
    // overwrite the stats the student is actually looking at.
    builder.addCase(getProgressStats.pending, (state, action) => {
      state.loading.stats = true;
      state.failed.stats = false;
      state.statsRequestId = action.meta.requestId;
      state.statsPeriod = action.meta.arg ?? DEFAULT_STATS_PERIOD;
    });
    builder.addCase(getProgressStats.fulfilled, (state, action) => {
      if (state.statsRequestId !== action.meta.requestId) return;

      state.stats = action.payload;
      state.loading.stats = false;
      state.fetched.stats = true;
      state.statsRequestId = null;
    });
    builder.addCase(getProgressStats.rejected, (state, action) => {
      if (state.statsRequestId !== action.meta.requestId) return;

      state.loading.stats = false;
      state.fetched.stats = true;
      state.failed.stats = true;
      state.statsRequestId = null;
    });

    // Rewards
    builder.addCase(getRewards.pending, (state) => {
      state.loading.rewards = true;
      state.failed.rewards = false;
    });
    builder.addCase(getRewards.fulfilled, (state, action) => {
      state.rewards = action.payload.rewards;
      state.loading.rewards = false;
      state.fetched.rewards = true;
    });
    builder.addCase(getRewards.rejected, (state) => {
      state.loading.rewards = false;
      state.fetched.rewards = true;
      state.failed.rewards = true;
    });

    // Vocabulary
    builder.addCase(getVocabularyProgress.pending, (state) => {
      state.loading.vocabulary = true;
      state.failed.vocabulary = false;
    });
    builder.addCase(getVocabularyProgress.fulfilled, (state, action) => {
      state.vocabulary = action.payload.data;
      state.loading.vocabulary = false;
      state.fetched.vocabulary = true;
    });
    builder.addCase(getVocabularyProgress.rejected, (state) => {
      state.loading.vocabulary = false;
      state.fetched.vocabulary = true;
      state.failed.vocabulary = true;
    });

    // Comparative
    builder.addCase(getComparativeStat.pending, (state) => {
      state.loading.comparative = true;
      state.failed.comparative = false;
    });
    builder.addCase(getComparativeStat.fulfilled, (state, action) => {
      state.comparative = action.payload.data;
      state.loading.comparative = false;
      state.fetched.comparative = true;
    });
    builder.addCase(getComparativeStat.rejected, (state) => {
      state.loading.comparative = false;
      state.fetched.comparative = true;
      state.failed.comparative = true;
    });

    // Goals
    builder.addCase(getGoals.pending, (state) => {
      state.loading.goals = true;
      state.failed.goals = false;
    });
    builder.addCase(getGoals.fulfilled, (state, action) => {
      state.goals = action.payload.data;
      state.loading.goals = false;
      state.fetched.goals = true;
    });
    builder.addCase(getGoals.rejected, (state) => {
      state.loading.goals = false;
      state.fetched.goals = true;
      state.failed.goals = true;
    });

    // Leaderboard
    // Keyed by the pair asked for rather than held as one board: the page has
    // two toggles, and flipping either back to something already loaded should
    // read the store instead of refetching.
    builder.addCase(getLeaderboard.pending, (state, action) => {
      const key = leaderboardKey(action.meta.arg.dimension, action.meta.arg.period);

      state.leaderboard.loading = key;
      // Only this pair's failure is cleared: asking for a different board is
      // not evidence that a previously failed one now works.
      if (state.leaderboard.failed === key) state.leaderboard.failed = null;
    });
    // Only the pair actually in flight may clear `loading`. Two requests can
    // overlap — the student flips a toggle before the first settles — and if
    // the older one cleared the flag on its way out, the page would see the
    // newer pair as neither loaded nor loading and ask for it a second time.
    builder.addCase(getLeaderboard.fulfilled, (state, action) => {
      const key = leaderboardKey(action.meta.arg.dimension, action.meta.arg.period);

      state.leaderboard.boards[key] = action.payload;
      if (state.leaderboard.loading === key) state.leaderboard.loading = null;
      if (state.leaderboard.failed === key) state.leaderboard.failed = null;
    });
    builder.addCase(getLeaderboard.rejected, (state, action) => {
      const key = leaderboardKey(action.meta.arg.dimension, action.meta.arg.period);

      state.leaderboard.failed = key;
      if (state.leaderboard.loading === key) state.leaderboard.loading = null;
    });
  }
});

const { reducer } = myProgressSlice;

const { leaderboardInvalidated } = myProgressSlice.actions;

const actions = {
  leaderboardInvalidated,
  getProgressStats,
  getRewards,
  getVocabularyProgress,
  getComparativeStat,
  getGoals,
  getLeaderboard
};

export { actions, reducer };
